import { Popover, Radio, Divider } from 'antd';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { useGetTranslation } from 'views/Conversation/Chat/components/MessageType/TextMsg';
import { useTranslate } from '@tolgee/react';
import { RightOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { useQuery } from '@tanstack/react-query';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useEffect, useRef, useState } from 'react';

interface KnowledgeSelectorItemProps {
  active: boolean;
  knowledge: Conversation.Message;
  onChange: (knowledgeQuery: Conversation.Message) => void;
}

const KnowledgeSelectorItem = (props: KnowledgeSelectorItemProps) => {
  const { active, knowledge, onChange } = props;
  const { getTranslation } = useGetTranslation();
  const systemLanguage = useSystemLanguageValue();
  const originText = knowledge.textContent?.text ?? '';

  const { data: translationContent } = useQuery({
    enabled: !!originText,
    queryKey: ['knowledgeQueryTranslationContent', originText],
    queryFn: async () => {
      const cache = getTranslation(knowledge?.textContent?.translationContent);
      if (cache) {
        return cache.text;
      }
      const content = await overseaTranslate(originText, systemLanguage, 'qaPairBuyerQuestion');
      return content;
    },
  });

  return (
    <div
      className={cx('p-2 flex items-start cursor-pointer', { 'bg-[#DDDFE1]': active })}
      data-active={active}
      onClick={() => {
        onChange(knowledge);
      }}
    >
      <Radio checked={active} />
      <div className="w-[28px] h-[28px] leading-[28px] rounded-sm bg-[#DA4F7A] text-center text-[#FFFFFF]">
        {knowledge.fromUserName?.charAt(0)}
      </div>
      <div className="flex flex-col ml-2">
        <div className="text-sm text-[#84868C]">
          {knowledge.sendTime ? dayjs(toNumber(knowledge.sendTime)).format('MM-DD HH:mm') : ''}
        </div>
        <div className="bg-[#FFFFFF] rounded p-2">
          <div>{originText}</div>
          {!!translationContent && (
            <>
              <Divider className="!my-2" />
              <div className="text-[#84868C]">{translationContent}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const KnowledgeSelector = (props: {
  value: string;
  onChange: (knowledgeQuery: Conversation.Message) => void;
  knowledgeSelections: Conversation.Message[];
}) => {
  const { value, knowledgeSelections, onChange } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && contentRef.current) {
      // 找到 active 的元素
      const activeElement = contentRef.current.querySelector('[data-active="true"]') as HTMLElement;
      if (activeElement && contentRef.current) {
        // 计算 activeElement 相对于 contentRef 的位置
        setTimeout(() => {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }
  }, [visible, value]);

  return (
    <Popover
      visible={visible}
      onVisibleChange={setVisible}
      content={
        <div className="w-[340px] max-h-[300px] overflow-y-auto" ref={contentRef}>
          {knowledgeSelections.map((knowledge) => (
            <KnowledgeSelectorItem
              key={knowledge.msgId}
              knowledge={knowledge}
              active={value === knowledge.msgId}
              onChange={onChange}
            />
          ))}
        </div>
      }
      overlayClassName={popoverCls}
      trigger="click"
      placement="right"
    >
      <div className="flex items-center gap-[2px] text-sm text-[var(--ant-primary-color)] flex-nowrap cursor-pointer hover:opacity-80">
        {t('选择其他问题')}
        <RightOutlined />
      </div>
    </Popover>
  );
};

const popoverCls = css`
  .ant-popover-inner-content {
    padding: 0;
    background-color: #eff1f5;
  }
  .ant-popover-arrow-content {
    background-color: #eff1f5;
  }
`;
